import { useForm } from "react-hook-form";
import {
  type RegisterFormData,
  registerFormSchema,
} from "./types/registerSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigation, useSubmit, Link } from "react-router-dom";
import { Code, Terminal, Monitor, Users, Building } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Register() {
  const { t } = useTranslation();
  const submit = useSubmit();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      surname: "",
      email: "",
      identificationNumber: "",
      parentName: "",
      parentSurname: "",
      parentIdentificationNumber: "",
      fullAddress: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = (data: RegisterFormData) => {
    submit(data, {
      method: "post",
      action: "/register",
      encType: "application/json",
    });
  };

  return (
    <div className="w-full max-w-7xl bg-white rounded-2xl shadow-2xl overflow-hidden">
      <div className="grid lg:grid-cols-5 min-h-[600px]">
        {/* Register Form Section - Takes 3/5 of the width */}
        <div className="lg:col-span-3 flex flex-col justify-center p-8 lg:p-12">
          <div className="w-full max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-primary rounded-lg p-2">
                  <Code className="h-6 w-6 text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-bold">{t('auth.registerTitle')}</h1>
              <p className="text-gray-600 mt-2">
                {t('auth.registerSubtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Grid for form fields - 2 columns on large screens */}
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                {/* Kişisel Bilgiler */}
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.name')}
                  </label>
                  <input
                    id="register-name"
                    type="text"
                    autoComplete="given-name"
                    {...register("name")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="surname"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.surname')}
                  </label>
                  <input
                    id="register-surname"
                    type="text"
                    autoComplete="family-name"
                    {...register("surname")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.surname && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.surname.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.email')}
                  </label>
                  <input
                    id="register-email"
                    type="email"
                    autoComplete="email"
                    {...register("email")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="identificationNumber"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.identificationNumber')}
                  </label>
                  <input
                    id="register-identificationNumber"
                    type="text"
                    maxLength={11}
                    {...register("identificationNumber")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.identificationNumber && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.identificationNumber.message}
                    </p>
                  )}
                </div>

                {/* Veli Bilgileri */}
                <div className="space-y-2">
                  <label
                    htmlFor="parentName"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.parentName')}
                  </label>
                  <input
                    id="register-parentName"
                    type="text"
                    {...register("parentName")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.parentName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.parentName.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="parentSurname"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.parentSurname')}
                  </label>
                  <input
                    id="register-parentSurname"
                    type="text"
                    {...register("parentSurname")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.parentSurname && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.parentSurname.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="parentIdentificationNumber"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.parentIdentificationNumber')}
                  </label>
                  <input
                    id="register-parentIdentificationNumber"
                    type="text"
                    maxLength={11}
                    {...register("parentIdentificationNumber")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.parentIdentificationNumber && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.parentIdentificationNumber.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="fullAddress"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.fullAddress')}
                  </label>
                  <input
                    id="register-fullAddress"
                    type="text"
                    {...register("fullAddress")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.fullAddress && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.fullAddress.message}
                    </p>
                  )}
                </div>

                {/* Şifre Alanları */}
                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.password')}
                  </label>
                  <input
                    id="register-password"
                    type="password"
                    {...register("password")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('auth.confirmPassword')}
                  </label>
                  <input
                    id="register-confirmPassword"
                    type="password"
                    {...register("confirmPassword")}
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full h-11 text-base font-medium text-white rounded-md transition-colors duration-300 ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-primary-600 cursor-pointer"
                  }`}
                >
                  {isSubmitting ? t('auth.registering') : t('auth.register')}
                </button>

                <div className="text-center mt-4 text-sm text-gray-600">
                  {t('auth.alreadyHaveAccount')}{" "}
                  <Link
                    to="/login"
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {t('auth.login')}
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Coding Illustration Section - Takes 2/5 of the width */}
        <div className="hidden lg:flex lg:col-span-2 bg-gradient-to-br from-primary-50 to-primary-100 items-center justify-center p-8 lg:p-12">
          <div className="relative w-full max-w-lg">
            {/* Main Illustration Container */}
            <div className="relative bg-white rounded-2xl shadow-xl p-6 border">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Terminal className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-gray-700">
                    {t('codeEditor.title')}
                  </span>
                </div>
                <div className="flex space-x-1">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
              </div>

              {/* Code Editor Mockup */}
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">1</span>
                  <span className="text-purple-400">class</span>
                  <span className="text-blue-400">Student</span>
                  <span className="text-white">{"{"}</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">2</span>
                  <span className="text-red-400">constructor</span>
                  <span className="text-yellow-400">(name, email)</span>
                  <span className="text-white">{"{"}</span>
                </div>
                <div className="flex items-center space-x-2 ml-8">
                  <span className="text-gray-500">3</span>
                  <span className="text-blue-300">this</span>
                  <span className="text-white">.name = name;</span>
                </div>
                <div className="flex items-center space-x-2 ml-8">
                  <span className="text-gray-500">4</span>
                  <span className="text-blue-300">this</span>
                  <span className="text-white">.email = email;</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">5</span>
                  <span className="text-white">{"}"}</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">6</span>
                  <span className="text-green-400">// Yeni öğrenci kaydı</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">7</span>
                  <span className="text-white">{"}"}</span>
                </div>
              </div>

              {/* Eğitim Şekilleri */}
              <div className="grid grid-cols-3 gap-3 mt-6">
                <div className="text-center p-3 bg-primary-50 rounded-lg">
                  <Monitor className="h-5 w-5 text-primary-600 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-600">
                    {t('education.online')}
                  </div>
                  <div className="text-xs text-primary-600">{t('education.training')}</div>
                </div>
                <div className="text-center p-3 bg-primary-100 rounded-lg">
                  <Users className="h-5 w-5 text-primary-700 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-700">
                    {t('education.hybrid')}
                  </div>
                  <div className="text-xs text-primary-700">{t('education.training')}</div>
                </div>
                <div className="text-center p-3 bg-primary-200 rounded-lg">
                  <Building className="h-5 w-5 text-primary-800 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-800">
                    {t('education.faceToFace')}
                  </div>
                  <div className="text-xs text-primary-800">{t('education.training')}</div>
                </div>
              </div>

              {/* Programming Languages */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {t('technologies.popular')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    "JavaScript",
                    "React",
                    "Node.js",
                    "Python",
                    "TypeScript",
                  ].map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-gradient-to-r from-primary to-primary-600 text-white text-xs rounded-full font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {/* Floating Elements */}
            <div className="absolute -top-3 -right-3 bg-primary-400 rounded-full p-2 shadow-lg animate-bounce">
              <Code className="h-5 w-5 text-primary-900" />
            </div>
            <div className="absolute -bottom-3 -left-3 bg-primary rounded-full p-2 shadow-lg animate-pulse">
              <Terminal className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
